import { forwardRef, useCallback, useState } from "react"

// --- Icons ---
import { ChevronDownIcon } from "../../tiptap-icons/chevron-down-icon"

// --- Hooks ---
import { useTiptapEditor } from "../../../hooks/use-tiptap-editor"

import { Button, ButtonGroup } from "../../tiptap-ui-primitive/button"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../tiptap-ui-primitive/dropdown-menu"
import { Card, CardBody } from "../../tiptap-ui-primitive/card"

/**
 * Custom table dropdown menu component for Tiptap editor.
 * Provides table insertion and manipulation commands.
 */
export const TableDropdownMenu = forwardRef((
  {
    editor: providedEditor,
    portal = false,
    onOpenChange,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const [isOpen, setIsOpen] = useState(false)

  const handleOpenChange = useCallback((open) => {
    if (!editor) return
    setIsOpen(open)
    onOpenChange?.(open)
  }, [editor, onOpenChange])

  const insertTable = useCallback(() => {
    if (!editor) return
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
    setIsOpen(false)
  }, [editor])

  const addRowBefore = useCallback(() => {
    if (!editor) return
    editor.chain().focus().addRowBefore().run()
    setIsOpen(false)
  }, [editor])

  const addRowAfter = useCallback(() => {
    if (!editor) return
    editor.chain().focus().addRowAfter().run()
    setIsOpen(false)
  }, [editor])

  const deleteRow = useCallback(() => {
    if (!editor) return
    editor.chain().focus().deleteRow().run()
    setIsOpen(false)
  }, [editor])

  const addColumnBefore = useCallback(() => {
    if (!editor) return
    editor.chain().focus().addColumnBefore().run()
    setIsOpen(false)
  }, [editor])

  const addColumnAfter = useCallback(() => {
    if (!editor) return
    editor.chain().focus().addColumnAfter().run()
    setIsOpen(false)
  }, [editor])

  const deleteColumn = useCallback(() => {
    if (!editor) return
    editor.chain().focus().deleteColumn().run()
    setIsOpen(false)
  }, [editor])

  const deleteTable = useCallback(() => {
    if (!editor) return
    editor.chain().focus().deleteTable().run()
    setIsOpen(false)
  }, [editor])

  const toggleHeaderRow = useCallback(() => {
    if (!editor) return
    editor.chain().focus().toggleHeaderRow().run()
    setIsOpen(false)
  }, [editor])

  const toggleHeaderColumn = useCallback(() => {
    if (!editor) return
    editor.chain().focus().toggleHeaderColumn().run()
    setIsOpen(false)
  }, [editor])

  const mergeCells = useCallback(() => {
    if (!editor) return
    editor.chain().focus().mergeCells().run()
    setIsOpen(false)
  }, [editor])

  const splitCell = useCallback(() => {
    if (!editor) return
    editor.chain().focus().splitCell().run()
    setIsOpen(false)
  }, [editor])

  const isInTable = editor?.isActive('table')

  return (
    <DropdownMenu modal open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          data-style="ghost"
          data-active-state={isInTable ? "on" : "off"}
          role="button"
          tabIndex={-1}
          aria-label="Table"
          tooltip="Table"
          {...buttonProps}
          ref={ref}>
          <span style={{ fontSize: '16px', fontWeight: 'bold' }}>⊞</span>
          <ChevronDownIcon className="tiptap-button-dropdown-small" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" portal={portal}>
        <Card>
          <CardBody>
            <ButtonGroup vertical>
              {!isInTable ? (
                <DropdownMenuItem asChild>
                  <Button
                    type="button"
                    data-style="ghost"
                    onClick={insertTable}
                    style={{ width: '100%', justifyContent: 'flex-start' }}>
                    Insert Table
                  </Button>
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem asChild>
                    <Button
                      type="button"
                      data-style="ghost"
                      onClick={addRowBefore}
                      style={{ width: '100%', justifyContent: 'flex-start' }}>
                      Add Row Before
                    </Button>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Button
                      type="button"
                      data-style="ghost"
                      onClick={addRowAfter}
                      style={{ width: '100%', justifyContent: 'flex-start' }}>
                      Add Row After
                    </Button>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Button
                      type="button"
                      data-style="ghost"
                      onClick={deleteRow}
                      style={{ width: '100%', justifyContent: 'flex-start' }}>
                      Delete Row
                    </Button>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Button
                      type="button"
                      data-style="ghost"
                      onClick={addColumnBefore}
                      style={{ width: '100%', justifyContent: 'flex-start' }}>
                      Add Column Before
                    </Button>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Button
                      type="button"
                      data-style="ghost"
                      onClick={addColumnAfter}
                      style={{ width: '100%', justifyContent: 'flex-start' }}>
                      Add Column After
                    </Button>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Button
                      type="button"
                      data-style="ghost"
                      onClick={deleteColumn}
                      style={{ width: '100%', justifyContent: 'flex-start' }}>
                      Delete Column
                    </Button>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Button
                      type="button"
                      data-style="ghost"
                      onClick={toggleHeaderRow}
                      style={{ width: '100%', justifyContent: 'flex-start' }}>
                      Toggle Header Row
                    </Button>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Button
                      type="button"
                      data-style="ghost"
                      onClick={toggleHeaderColumn}
                      style={{ width: '100%', justifyContent: 'flex-start' }}>
                      Toggle Header Column
                    </Button>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Button
                      type="button"
                      data-style="ghost"
                      onClick={mergeCells}
                      style={{ width: '100%', justifyContent: 'flex-start' }}>
                      Merge Cells
                    </Button>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Button
                      type="button"
                      data-style="ghost"
                      onClick={splitCell}
                      style={{ width: '100%', justifyContent: 'flex-start' }}>
                      Split Cell
                    </Button>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Button
                      type="button"
                      data-style="ghost"
                      onClick={deleteTable}
                      style={{ width: '100%', justifyContent: 'flex-start', color: 'red' }}>
                      Delete Table
                    </Button>
                  </DropdownMenuItem>
                </>
              )}
            </ButtonGroup>
          </CardBody>
        </Card>
      </DropdownMenuContent>
    </DropdownMenu>
  );
})

TableDropdownMenu.displayName = "TableDropdownMenu"

export default TableDropdownMenu




